import React, { useCallback, useEffect, useState } from 'react';
import { Box, Flex, useDisclosure } from '@chakra-ui/react';
import { MaintenanceAdminUsersTable } from '../components/organisms/Maintenance/Table/MaintenanceAdminUsersTable';
import AdminUsersCreateWindow from '../components/organisms/Maintenance/CreateWindow/AdminUsersCreateWindow';
import { AdminUsersType } from '../components/organisms/Maintenance/Table/MaintenanceAdminUsersTable';
import { ConfirmationDialog } from '../components/organisms/ConfirmationDialog';
import { AdminUsersBrowseWindow } from '../components/organisms/Maintenance/BrowseWindow/AdminUsersBrowseWindow';
import AdminUsersEditWindow from '../components/organisms/Maintenance/EditWindow/AdminUsersEditWindow';
import {
  MaintenanceHeader,
  selectTabEnum
} from '../components/organisms/Maintenance/MaintenanceHeader';
import { MaintenanceTitle } from '../components/organisms/Maintenance/MaintenanceTitle';
import AdminUsersSearchBox from '../components/organisms/Maintenance/SearchWindow/AdminUsersSearchBox';
import { DeleteAdminUsersApi } from '../apis/DeleteMaintenance.api';
import { GetAdminUsersApi } from '../apis/GetMaintenance.api';
import { chooseDataStateType } from '../components/molecules/MaintenanceModalDispLabels';

export type AdminUsersStateType = {
  type: 'adminUsers';
  adminUsers: AdminUsersType;
};

export const TableColumnEnum = {
  id: 'id',
  user_no: 'user_no',
  name: 'name',
  kana: 'kana',
  role: 'role',
  created_at: 'created_at',
  updated_at: 'updated_at'
};

export type AdminUsersPostDataType = {
  user_no?: number;
  name?: string;
  kana?: string;
  group?: string;
  password?: string;
  role?: number;
};

export const RoleEnum = {
  Any: 0,
  MastaMente: 1,
  Notice: 2,
  Browse: 3,
  Suspension: 4
};

export type AdminUsersArrayStateType = {
  type: 'adminUsers';
  adminUsers: AdminUsersType[] | undefined;
};

export interface AdminUsersSearchCondition {
  sort: { key: string; sortType: boolean };
  crewNumber: { compare: string; number: string };
  crewName: string;
  crewFurigana: string;
  role: string;
  dateCreatedFrom: string;
  dateCreatedTo: string;
  dateUpdatedFrom: string;
  dateUpdatedTo: string;
}

export const AdminUsersMaintenance: React.FC = () => {
  const [query, setQuery] = useState<AdminUsersSearchCondition>({
    sort: { key: '', sortType: false },
    crewNumber: { compare: '1', number: '' },
    crewName: '',
    crewFurigana: '',
    role: RoleEnum.Any.toString(),
    dateCreatedFrom: '',
    dateCreatedTo: '',
    dateUpdatedFrom: '',
    dateUpdatedTo: ''
  });

  const StateUpdate = (data: chooseDataStateType) => {
    if (data.type === 'adminUsers') {
      setChooseData(data.adminUsers);
    }
  };

  const DeleteApi = async () => {
    if (chooseData?.id !== undefined) {
      const response = await DeleteAdminUsersApi(chooseData.id);
      if (response.success) {
        setMaintenanceDatas(response.data as AdminUsersType[]);
        CreateOrUpdateStateSwitch();
      }
    }
  };

  const {
    isOpen: isOpenCreate,
    onOpen: onOpenCreate,
    onClose: onCloseCreate
  } = useDisclosure();
  const {
    isOpen: isOpenBrowse,
    onOpen: onOpenBrowse,
    onClose: onCloseBrowse
  } = useDisclosure();
  const {
    isOpen: isOpenEdit,
    onOpen: onOpenEdit,
    onClose: onCloseEdit
  } = useDisclosure();
  const {
    isOpen: isOpenConfirm,
    onOpen: onToggle,
    onClose: onCloseConfirm
  } = useDisclosure();

  const [chooseData, setChooseData] = useState<AdminUsersType | undefined>();
  const [maintenanceDatas, setMaintenanceDatas] = useState<
    AdminUsersType[] | undefined
  >();
  const [createOrUpdate, setCreateOrUpdate] = useState(false);

  const CreateOrUpdateStateSwitch = useCallback(() => {
    setQuery({
      sort: { key: '', sortType: false },
      crewNumber: { compare: '1', number: '' },
      crewName: '',
      crewFurigana: '',
      role: RoleEnum.Any.toString(),
      dateCreatedFrom: '',
      dateCreatedTo: '',
      dateUpdatedFrom: '',
      dateUpdatedTo: ''
    });
    setCreateOrUpdate(!createOrUpdate);
  }, [createOrUpdate]);

  const [searchSwitch, setSearchSwitch] = useState(false);

  const SearchStateSwitch = useCallback(() => {
    setSearchSwitch(!searchSwitch);
  }, [searchSwitch]);

  const GetApi = async () => {
    const response = await GetAdminUsersApi();
    if (response.success) {
      setMaintenanceDatas(response.data as AdminUsersType[]);
    }
  };

  useEffect(() => {
    GetApi();
  }, []);

  return (
    <>
      <Flex direction='column' h='100vh'>
        <MaintenanceHeader selectTab={selectTabEnum.AdminUsers} />
        <MaintenanceTitle
          selectTab={selectTabEnum.AdminUsers}
          onOpen={onOpenCreate}
        />
        <Box
          position='relative'
          display='grid'
          gridTemplateRows='0.1fr 2fr 0.1fr'
          gridTemplateColumns='3fr 1fr'
          h='100%'
        >
          <MaintenanceAdminUsersTable
            AdminUsersDatas={maintenanceDatas}
            setMaintenanceDatas={setMaintenanceDatas}
            setChooseData={StateUpdate}
            onOpenBrowse={onOpenBrowse}
            onOpenEdit={onOpenEdit}
            onToggle={onToggle}
            Query={query}
            setQuery={setQuery}
            createOrUpdate={createOrUpdate}
            searchSwitch={searchSwitch}
          />

          <AdminUsersSearchBox
            setMaintenanceDatas={setMaintenanceDatas}
            Query={query}
            setQuery={setQuery}
            createOrUpdate={createOrUpdate}
            SearchStateSwitch={SearchStateSwitch}
          />

          {isOpenCreate && (
            <AdminUsersCreateWindow
              isOpen={isOpenCreate}
              onClose={onCloseCreate}
              setMaintenanceDatas={setMaintenanceDatas}
              CreateOrUpdateStateSwitch={CreateOrUpdateStateSwitch}
            />
          )}

          {isOpenBrowse && (
            <AdminUsersBrowseWindow
              isOpen={isOpenBrowse}
              onClose={onCloseBrowse}
              BrowseData={chooseData}
            />
          )}

          {isOpenEdit && (
            <AdminUsersEditWindow
              isOpen={isOpenEdit}
              onClose={onCloseEdit}
              EditData={chooseData}
              setMaintenanceDatas={setMaintenanceDatas}
              CreateOrUpdateStateSwitch={CreateOrUpdateStateSwitch}
            />
          )}
          {isOpenConfirm && (
            <ConfirmationDialog
              isOpen={isOpenConfirm}
              onClose={onCloseConfirm}
              dispMessage={'本当に削除しますか？'}
              DeleteApi={DeleteApi}
            />
          )}
        </Box>
      </Flex>
    </>
  );
};
